import { useState, useEffect } from "react";
import { api } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LifeBuoy, CheckCircle2, Clock } from "lucide-react";

export default function SupportTicketDialog({ open, onOpenChange }) {
  const [type, setType] = useState("refund");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);

  useEffect(() => {
    if (open) {
      api.get("/support/tickets/mine").then((r) => setTickets(r.data.tickets)).catch(() => {});
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (subject.trim().length < 3) {
      toast.error("Please add a short subject");
      return;
    }
    if (message.trim().length < 10) {
      toast.error("Please describe your issue in a bit more detail");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/support/tickets", { type, subject: subject.trim(), message: message.trim() });
      toast.success("Request submitted — we'll get back to you soon!");
      setSubject("");
      setMessage("");
      const r = await api.get("/support/tickets/mine");
      setTickets(r.data.tickets);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const typeBtn = (value, label) => (
    <button
      type="button"
      className={`px-3 py-2.5 rounded-xl text-sm border transition-colors ${
        type === value
          ? "border-primary bg-primary/10 text-primary"
          : "border-white/10 bg-white/5 text-muted-foreground hover:bg-white/10"
      }`}
      onClick={() => setType(value)}
      data-testid={`ticket-type-${value}`}
    >
      {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-white/10 max-h-[85vh] overflow-y-auto" data-testid="support-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <LifeBuoy className="w-5 h-5 text-primary" /> Refunds &amp; Complaints
          </DialogTitle>
          <DialogDescription>
            Tell us what went wrong. Every request is reviewed by our team — you'll get a notification and email when it's resolved.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {typeBtn("refund", "💸 Refund request")}
            {typeBtn("complaint", "⚠️ Complaint")}
          </div>
          <div>
            <Label htmlFor="ticket-subject">Subject</Label>
            <Input
              id="ticket-subject"
              className="mt-1 bg-white/5 border-white/10"
              placeholder={type === "refund" ? "e.g. Refund for unused credits" : "e.g. Issue with another member"}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={120}
              required
              data-testid="ticket-subject-input"
            />
          </div>
          <div>
            <Label htmlFor="ticket-message">Details</Label>
            <Textarea
              id="ticket-message"
              className="mt-1 bg-white/5 border-white/10 min-h-[110px]"
              placeholder="Describe what happened, including any dates, amounts or member names that help us investigate..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={3000}
              required
              data-testid="ticket-message-input"
            />
          </div>
          <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={submitting} data-testid="ticket-submit-btn">
            {submitting ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              "Submit request"
            )}
          </Button>
        </form>

        {tickets.length > 0 && (
          <div className="mt-2 pt-4 border-t border-white/10" data-testid="my-tickets-list">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Your recent requests</p>
            <div className="space-y-2">
              {tickets.slice(0, 5).map((t) => (
                <div key={t.ticket_id} className="flex items-start gap-2 p-3 rounded-lg bg-white/5 border border-white/10" data-testid={`my-ticket-${t.ticket_id}`}>
                  {t.status === "resolved" ? (
                    <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                  ) : (
                    <Clock className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium truncate">{t.subject}</p>
                      <span
                        className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                          t.status === "resolved" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"
                        }`}
                      >
                        {t.status === "resolved" ? "RESOLVED" : "OPEN"}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {t.type === "refund" ? "Refund request" : "Complaint"} • {new Date(t.created_at).toLocaleDateString()}
                    </p>
                    {t.resolution && <p className="text-xs text-green-400/80 mt-1">{t.resolution}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
